import React, { useState, useEffect, useCallback } from 'react';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import { api } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  Button,
  LinearProgress,
  Switch,
  FormControlLabel,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import {
  SmartToy,
  TrendingUp,
  Psychology,
  EmojiObjects,
  Engineering,
  Speed,
  People,
  IntegrationInstructions,
  Settings,
  PlayArrow,
  CheckCircle,
  Error,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Static icon and color mapping for agents
const AGENT_UI_CONFIG: Record<string, { icon: React.ReactElement; color: string }> = {
  strategic_forecaster: { icon: <TrendingUp />, color: '#1976d2' },
  game_theorist: { icon: <Psychology />, color: '#9c27b0' },
  creative_catalyst: { icon: <EmojiObjects />, color: '#ed6c02' },
  technical_architect: { icon: <Engineering />, color: '#0288d1' },
  resource_optimizer: { icon: <Speed />, color: '#2e7d32' },
  human_insight: { icon: <People />, color: '#d32f2f' },
  knowledge_integrator: { icon: <IntegrationInstructions />, color: '#1565c0' },
};

interface Agent {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'inactive' | 'error';
  enabled: boolean;
  autonomy: number;
  tasksCompleted: number;
  tasksActive: number;
  successRate: number;
  lastActive: string;
  description: string;
}

// Task type options for agents
const TASK_TYPES = [
  { value: 'demand_forecast', label: 'Demand Forecast' },
  { value: 'disruption_analysis', label: 'Disruption Analysis' },
  { value: 'supplier_evaluation', label: 'Supplier Evaluation' },
  { value: 'inventory_optimization', label: 'Inventory Optimization' },
  { value: 'route_optimization', label: 'Route Optimization' },
  { value: 'cost_analysis', label: 'Cost Analysis' },
  { value: 'risk_assessment', label: 'Risk Assessment' },
];

const AgentsDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  
  // Config dialog state
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [autonomyValue, setAutonomyValue] = useState(50);
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Task dialog state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskAgentId, setTaskAgentId] = useState<string | null>(null);
  const [taskType, setTaskType] = useState('demand_forecast');
  const [taskParameters, setTaskParameters] = useState('');
  const [runningTask, setRunningTask] = useState(false);
  const [taskResult, setTaskResult] = useState<any>(null);

  // Fetch agents from API
  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.agents.list();
      if (response.data && response.data.length > 0) {
        setAgents(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Handle agent enable/disable toggle
  const handleToggleAgent = async (agentId: string) => {
    const agent = agents.find(a => a.id === agentId);
    if (!agent) return;

    const newEnabled = !agent.enabled;
    
    // Optimistic update
    setAgents(agents.map(a =>
      a.id === agentId
        ? { ...a, enabled: newEnabled, status: newEnabled ? 'active' : 'inactive' }
        : a
    ));

    try {
      if (newEnabled) {
        await api.agents.enable(agentId);
      } else {
        await api.agents.disable(agentId);
      }
    } catch (err: any) {
      console.error('Failed to toggle agent:', err);
      // Revert on error
      setAgents(agents.map(a =>
        a.id === agentId
          ? { ...a, enabled: !newEnabled, status: !newEnabled ? 'active' : 'inactive' }
          : a
      ));
      setError(err.message || 'Failed to update agent status');
    }
  };

  // Open configuration dialog for an agent
  const handleOpenConfig = (agent: Agent) => {
    setSelectedAgent(agent);
    setAutonomyValue(agent.autonomy);
    setConfigDialogOpen(true);
  };

  // Save agent configuration
  const handleSaveConfig = async () => {
    if (!selectedAgent) return;
    
    setSavingConfig(true);
    try {
      await api.agents.configure(selectedAgent.id, { autonomy: autonomyValue });
      
      // Update local state
      setAgents(agents.map(a =>
        a.id === selectedAgent.id
          ? { ...a, autonomy: autonomyValue }
          : a
      ));
      
      setConfigDialogOpen(false);
      setSelectedAgent(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  // Open task dialog
  const handleOpenTaskDialog = (agentId: string) => {
    setTaskAgentId(agentId);
    setTaskType('demand_forecast');
    setTaskParameters('');
    setTaskResult(null);
    setTaskDialogOpen(true);
  };

  // Run a task on an agent
  const handleRunTask = async () => {
    if (!taskAgentId) return;
    
    setRunningTask(true);
    setTaskResult(null);
    
    try {
      let params = {};
      if (taskParameters.trim()) {
        try {
          params = JSON.parse(taskParameters);
        } catch {
          params = { query: taskParameters };
        }
      }
      
      const response = await api.agentTasks.trigger(taskAgentId, taskType, params);
      setTaskResult(response.data);
      
      // Refresh agents to update task counts
      fetchAgents();
    } catch (err: any) {
      setTaskResult({ error: err.message || 'Failed to run task' });
    } finally {
      setRunningTask(false);
    }
  };

  // Get icon and color for an agent
  const getAgentUI = (agentId: string) => {
    return AGENT_UI_CONFIG[agentId] || { icon: <SmartToy />, color: theme.palette.primary.main };
  };

  const activeAgents = agents.filter(a => a.enabled).length;
  const totalTasks = agents.reduce((sum, a) => sum + (a.tasksActive || 0), 0);
  const avgSuccessRate = agents.length > 0 
    ? agents.reduce((sum, a) => sum + (a.successRate || 0), 0) / agents.length 
    : 0;

  if (loading) {
    return <LoadingState message="Loading AI agents..." />;
  }

  return (
    <Box>
      {error && <ErrorAlert error={error} onRetry={fetchAgents} />}
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          AI Agent Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor and configure your AI agents
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {activeAgents}/{agents.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Agents
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                {totalTasks}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Tasks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                {avgSuccessRate.toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Success Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Agent Cards */}
      <Grid container spacing={3}>
        {agents.map((agent) => {
          const { icon: agentIcon, color: agentColor } = getAgentUI(agent.id);
          return (
          <Grid item xs={12} md={6} lg={4} key={agent.id}>
            <Card
              sx={{
                height: '100%',
                border: 2,
                borderColor: agent.enabled ? agentColor : 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: alpha(agentColor, 0.1),
                        color: agentColor,
                        width: 48,
                        height: 48,
                      }}
                    >
                      {agentIcon}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {agent.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {agent.type}
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={agent.enabled}
                        onChange={() => handleToggleAgent(agent.id)}
                        color="primary"
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </Box>

                {/* Status */}
                <Box sx={{ mb: 2 }}>
                  <Chip
                    icon={agent.status === 'active' ? <CheckCircle /> : <Error />}
                    label={agent.status.toUpperCase()}
                    size="small"
                    color={agent.status === 'active' ? 'success' : 'default'}
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Last active: {agent.lastActive}
                  </Typography>
                </Box>

                {/* Description */}
                <Typography variant="body2" color="text.secondary" paragraph>
                  {agent.description}
                </Typography>

                {/* Metrics */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Tasks Completed
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {agent.tasksCompleted}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Active Tasks
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {agent.tasksActive}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Success Rate */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Success Rate
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {agent.successRate || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={agent.successRate || 0}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(agentColor, 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: agentColor,
                      },
                    }}
                  />
                </Box>

                {/* Autonomy Level */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Autonomy Level
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {agent.autonomy || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={agent.autonomy || 0}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.info.main,
                      },
                    }}
                  />
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<Settings />}
                    onClick={() => handleOpenConfig(agent)}
                  >
                    Configure
                  </Button>
                  <Tooltip title={agent.enabled ? "Run Task" : "Enable agent first"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenTaskDialog(agent.id)}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: alpha(agentColor, 0.1),
                          },
                        }}
                        disabled={!agent.enabled}
                      >
                        <PlayArrow />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {/* Configuration Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAgent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(getAgentUI(selectedAgent.id).color, 0.1), color: getAgentUI(selectedAgent.id).color }}>
                    {getAgentUI(selectedAgent.id).icon}
                  </Avatar>
                  <Typography variant="h6">Configure {selectedAgent.name}</Typography>
                </Box>
                <IconButton onClick={() => setConfigDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Autonomy Level
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Higher autonomy allows the agent to make decisions without human approval. 
                  Lower autonomy requires more human oversight.
                </Typography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={autonomyValue}
                    onChange={(_, value) => setAutonomyValue(value as number)}
                    min={0}
                    max={100}
                    step={5}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 25, label: '25%' },
                      { value: 50, label: '50%' },
                      { value: 75, label: '75%' },
                      { value: 100, label: '100%' },
                    ]}
                    valueLabelDisplay="on"
                    sx={{ mt: 4 }}
                  />
                </Box>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  {autonomyValue <= 25 && (
                    <Alert severity="info" sx={{ mb: 0 }}>
                      <strong>Supervised Mode:</strong> Agent requires approval for all actions.
                    </Alert>
                  )}
                  {autonomyValue > 25 && autonomyValue <= 50 && (
                    <Alert severity="info" sx={{ mb: 0 }}>
                      <strong>Guided Mode:</strong> Agent can handle routine tasks, major decisions need approval.
                    </Alert>
                  )}
                  {autonomyValue > 50 && autonomyValue <= 75 && (
                    <Alert severity="warning" sx={{ mb: 0 }}>
                      <strong>Semi-Autonomous:</strong> Agent operates independently with periodic check-ins.
                    </Alert>
                  )}
                  {autonomyValue > 75 && (
                    <Alert severity="error" sx={{ mb: 0 }}>
                      <strong>Fully Autonomous:</strong> Agent makes decisions without human intervention.
                    </Alert>
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Agent Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Tasks Completed</Typography>
                    <Typography variant="h6">{selectedAgent.tasksCompleted}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Active Tasks</Typography>
                    <Typography variant="h6">{selectedAgent.tasksActive}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                    <Typography variant="h6">{selectedAgent.successRate}%</Typography>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={savingConfig}
              >
                {savingConfig ? 'Saving...' : 'Save Configuration'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Task Dialog */}
      <Dialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Run Agent Task</Typography>
            <IconButton onClick={() => setTaskDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Task Type</InputLabel>
                <Select
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value)}
                  label="Task Type"
                >
                  {TASK_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Parameters (JSON or plain text)"
                multiline
                rows={4}
                value={taskParameters}
                onChange={(e) => setTaskParameters(e.target.value)}
                placeholder='{"scenario": "demand spike", "region": "EMEA"}'
                helperText="Enter task parameters as JSON or plain text query"
              />
            </Grid>
          </Grid>

          {taskResult && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Result
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  bgcolor: taskResult.error ? 'error.light' : 'success.light',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 300,
                  fontSize: '0.85rem',
                }}
              >
                {JSON.stringify(taskResult, null, 2)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={runningTask ? null : <PlayArrow />}
            onClick={handleRunTask}
            disabled={runningTask}
          >
            {runningTask ? 'Running...' : 'Run Task'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentsDashboard;
